import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import deleteProject from 'api/project/deleteProject';
import API_END_POINT from 'constants/apiEndPoints';
import useModal from 'hooks/useModal';
import useUser from 'hooks/useUser';

interface FormValues {
  text: string;
}

interface useDeleteProjectProps {
  ownerName: string;
  projectName: string;
}

const useDeleteProject = ({ ownerName, projectName }: useDeleteProjectProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const modal = useModal();
  const useFormReturn = useForm<FormValues>();

  const { data } = useUser();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: [API_END_POINT.PROJECT.FETCH_PROJECTS, data!.userEmail] });
  };

  const projectDeleteMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: invalidateQueries,
  });

  const onValid = ({ text }: FormValues) => {
    if (text !== `${ownerName}/${projectName}`) {
      useFormReturn.setError(
        'text',
        {
          message: '입력하신 내용이 일치하지 않습니다.',
        },
        {
          shouldFocus: true,
        }
      );
      return;
    }

    projectDeleteMutation.mutate(
      { ownerName, projectName },
      {
        onSuccess: () => {
          modal.close();
          toast.success('삭제되었습니다.');
          navigate('/');
        },
        onError: () => {
          toast.error('삭제에 실패했습니다.');
        },
      }
    );
  };

  return {
    ...useFormReturn,
    isLoading: projectDeleteMutation.isPending || useFormReturn.formState.isSubmitting,
    onValid,
  };
};

export default useDeleteProject;
